import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {generatePath, useHistory, useLocation, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';

import {fetchProductsDetail, validateCodesByArray} from 'api';
import {
    createTransferDocument,
    fetchDocumentsGrid,
    fetchStorageLocation,
    fetchTinLegalName,
    fetchTransferDocument,
    updateTransferDocument,
    updateTransferDocumentStatus,
} from 'api/documents';
import {ReactComponent as CheckIcon} from 'assets/svg/check.svg';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {BackTo} from 'components/common/BackTo';
import {ConfirmNavigationModal} from 'components/pages/DocumentsCreate/InvoiceNew/ConfirmNavigationModal';
import {CustomerCompanyNameOrTIN} from 'components/pages/DocumentsCreate/InvoiceNew/FormsData/selectOptionsComponents/CustomerCompanyNameOrTIN';
import {useNavigationBlocker} from 'components/pages/DocumentsCreate/InvoiceNew/hooks/useNavigationBlocker';
import {
    CloseAndLinkBack,
    CloseAndLinkBackIcon,
    CloseAndLinkBackText,
    HeadControl,
    InfoSection,
    InvoiceId,
    InvoiceIdContainer,
    InvoiceIdCopyButton,
    Root,
    Separator,
    Status,
    Wrapper,
} from 'components/pages/DocumentsCreate/InvoiceNew/styled';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {DatePicker} from 'components/ui-kit/DatePicker';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {AsyncSelect, SelectOption} from 'components/ui-kit/Select';
import {Spinner} from 'components/ui-kit/Spinner';
import {Stepper} from 'components/ui-kit/Stepper';
import {CURRENCY} from 'core/constants/currency';
import {ALPHA_2_COUNTRY} from 'core/constants/env';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {DocumentReportType, DocumentStatus} from 'core/dictionaries';
import {RestError, showRestError} from 'core/errors';
import {ROUTE} from 'core/routes';
import {permissions$, profile$} from 'models/auth';
import {checkPermission} from 'models/documents/invoices/common/helper';
import {
    TransferDocumentCustomer,
    TransferDocumentLine,
    TransferDocumentParty,
    TransferDocumentRequest,
    TransferDocumentStorageLocation,
} from 'types';
import {DocumentsGridRawRowExtended} from 'types/invoices';
import {CodeResponseType} from 'types/validation';
import {useClipboard} from 'utils/custom-hooks';

import {TransferDocumentLinesStep} from './LinesStep';
import {
    FieldError,
    Footer,
    Grid,
    HeaderActions,
    HeaderLabel,
    Section,
    SectionTitle,
    StepContent,
} from './styled';
import {
    areTransferDocumentLinesValid,
    collectTransferDocumentCodes,
    collectTransferDocumentLinesWithData,
    flattenTransferDocumentLines,
    restoreTransferDocumentLines,
} from './transportCodes';

type Side = 'INCOMING' | 'OUTGOING';

interface PartyOption extends SelectOption<string> {
    companyName: string;
    tin: string;
    postalAddress: string;
    streetName: string;
    cityName: string;
    postalZone: string;
    countryCode: string;
}

interface StorageOption extends SelectOption<string> {
    storageLocation: TransferDocumentStorageLocation;
}

const EMPTY_PARTY: TransferDocumentParty = {
    companyName: '',
    tin: '',
    postalAddress: '',
    streetName: '',
    cityName: '',
    postalZone: '',
    countryCode: '',
};

const EMPTY_STORAGE: TransferDocumentStorageLocation = {
    storageLocationName: '',
    storageLocationAddress: '',
    streetName: '',
    cityName: '',
    postalZone: '',
    countryCode: '',
};

const EMPTY_CUSTOMER: TransferDocumentCustomer = {
    ...EMPTY_PARTY,
    customerStorageLocation: EMPTY_STORAGE,
};

const roundMoney = (value: number) => Math.round(value * 100) / 100;
const DEFAULT_CURRENCY = CURRENCY === '—' ? '' : CURRENCY;
const TRANSFER_DOCUMENT_STATUS_POLL_INTERVAL_MS = 1000;
const TRANSFER_DOCUMENT_STATUS_POLL_MAX_ATTEMPTS = 60;
const TRANSFER_DOCUMENT_STATUS_TIMEOUT = 'TRANSFER_DOCUMENT_STATUS_TIMEOUT';
const normalizeAddressPart = (value?: string) => (value && value !== 'NOT_AVAILABLE' ? value : '');
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchTransferDocumentGridRow = async (reportId: string) => {
    const result = await fetchDocumentsGrid({
        sortings: [{id: 'createdAt', desc: true}],
        filters: [],
        paging: {
            pageNumber: 0,
            pageSize: 3,
        },
        search: reportId,
        updOnly: true,
    });
    return result.content.find((item) => item.reportId === reportId) as
        | DocumentsGridRawRowExtended
        | undefined;
};

const TRANSFER_DOCUMENT_PENDING_STATUSES: DocumentStatus[] = [
    DocumentStatus.PROCESSING,
    DocumentStatus.SUBMITTED,
];

const isTransferDocumentPending = (status?: DocumentStatus) =>
    !status || TRANSFER_DOCUMENT_PENDING_STATUSES.includes(status);

const isTransferDocumentReady = (
    document?: DocumentsGridRawRowExtended,
): document is DocumentsGridRawRowExtended =>
    document?.status === DocumentStatus.DRAFT || document?.status === DocumentStatus.RETURNED;

const waitUntilTransferDocumentSettled = async (
    reportId: string,
    attemptsLeft = TRANSFER_DOCUMENT_STATUS_POLL_MAX_ATTEMPTS,
): Promise<DocumentsGridRawRowExtended> => {
    const document = await fetchTransferDocumentGridRow(reportId);
    if (document && !isTransferDocumentPending(document.status)) {
        return document;
    }
    if (attemptsLeft <= 1) {
        throw new Error(TRANSFER_DOCUMENT_STATUS_TIMEOUT);
    }
    await delay(TRANSFER_DOCUMENT_STATUS_POLL_INTERVAL_MS);
    return waitUntilTransferDocumentSettled(reportId, attemptsLeft - 1);
};

const buildPostalAddress = (...parts: Array<string | undefined>) =>
    parts
        .map(normalizeAddressPart)
        .filter(Boolean)
        .join(' ');

const normalizeParty = (party: TransferDocumentParty): TransferDocumentParty => ({
    ...party,
    postalAddress:
        party.postalAddress ||
        buildPostalAddress(party.postalZone, party.countryCode, party.cityName, party.streetName) ||
        'NOT_AVAILABLE',
    streetName: party.streetName || 'NOT_AVAILABLE',
    cityName: party.cityName || 'NOT_AVAILABLE',
    postalZone: party.postalZone || 'NOT_AVAILABLE',
    countryCode: party.countryCode || ALPHA_2_COUNTRY,
});

export const TransferDocumentPage = () => {
    const {t} = useTranslation();
    const history = useHistory();
    const location = useLocation<{from?: string; side?: Side}>();
    const {id} = useParams<{id?: string}>();
    const profile = useStore(profile$);
    const permissions = useStore(permissions$);
    const [activeStep, setActiveStep] = useState(0);
    const [status, setStatus] = useState<DocumentStatus>(DocumentStatus.DRAFT);
    const [loadedDocumentId, setLoadedDocumentId] = useState<string | null>(null);
    const [side, setSide] = useState<Side>(location.state?.side ?? 'OUTGOING');
    const [issueDate, setIssueDate] = useState<Date | null>(new Date());
    const currency = DEFAULT_CURRENCY;
    const [supplier, setSupplier] = useState<TransferDocumentParty>(EMPTY_PARTY);
    const [customer, setCustomer] = useState<TransferDocumentCustomer>(EMPTY_CUSTOMER);
    const [lines, setLines] = useState<TransferDocumentLine[]>([]);
    const [pending, setPending] = useState(Boolean(id));
    const [showErrors, setShowErrors] = useState(false);
    const canPrepare = Boolean(permissions.MPL_ISEMT_INVOICE_SHIPMENT_PREPARE);
    const copy = useClipboard();
    const backPath = location.state?.from || ROUTE.DOCUMENTS_TRANSFER_OUTGOING;

    const statusKnown = !id || loadedDocumentId === id;
    const editable = statusKnown && (!id || checkPermission(status, side, permissions, 'edit'));
    const {
        showModal: showNavigationModal,
        handleConfirm: confirmNavigation,
        handleCancel: cancelNavigation,
        allowNavigation,
    } = useNavigationBlocker({
        when: editable && (Boolean(id) || canPrepare),
    });
    const totals = useMemo(() => {
        const requestLines = flattenTransferDocumentLines(lines);
        const taxExclusiveAmount = roundMoney(
            requestLines.reduce((sum, line) => sum + Number(line.lineAmount || 0), 0),
        );
        const taxAmount = roundMoney(
            requestLines.reduce((sum, line) => sum + Number(line.lineTaxAmount || 0), 0),
        );
        const taxInclusiveAmount = roundMoney(taxExclusiveAmount + taxAmount);
        return {
            taxExclusiveAmount,
            taxAmount,
            taxInclusiveAmount,
            payableAmount: taxInclusiveAmount,
        };
    }, [lines]);

    useEffect(() => {
        if (!id && !canPrepare) {
            history.replace(ROUTE.NO_ACCESS);
        }
    }, [canPrepare, history, id]);

    useEffect(() => {
        const legalPerson = profile?.legalPerson;
        if (!legalPerson || id) return;
        setSupplier({
            companyName: legalPerson.company_name ?? '',
            tin: legalPerson.tin ?? '',
            postalAddress: legalPerson.legal_address ?? '',
            streetName: legalPerson.street || legalPerson.legal_address || '',
            cityName: legalPerson.city ?? '',
            postalZone: legalPerson.postal_code ?? '',
            countryCode: legalPerson.country ?? '',
        });
    }, [id, profile]);

    useEffect(() => {
        if (!id) return;
        let mounted = true;
        setPending(true);
        setLoadedDocumentId(null);
        Promise.allSettled([fetchTransferDocument(id), fetchTransferDocumentGridRow(id)])
            .then(async ([bodyResult, documentResult]) => {
                if (!mounted) return;

                if (bodyResult.status === 'rejected') {
                    showRestError(bodyResult.reason as RestError);
                    return;
                }

                const body = bodyResult.value;
                setIssueDate(body.issueDate ? new Date(body.issueDate) : null);
                setSupplier(body.supplier || EMPTY_PARTY);
                setCustomer(body.customer || EMPTY_CUSTOMER);
                const bodyLines = body.transferDocumentLines || [];
                let validatedCodes: CodeResponseType['info'][] = [];
                if (bodyLines.length) {
                    try {
                        const validation = await validateCodesByArray(
                            bodyLines.map((line) => line.markingCode),
                            DocumentReportType.TRANSFER_DOCUMENT,
                        );
                        validatedCodes = validation.data.data
                            .map(({info}) => info)
                            .filter((info) => Boolean(info?.identityCode));
                    } catch (error) {
                        console.error(error);
                    }
                }
                const restoredLines = restoreTransferDocumentLines(bodyLines, validatedCodes);
                const unknownParents = collectTransferDocumentLinesWithData(restoredLines).filter(
                    (line) => line.subRows?.length && !line.packagingType,
                );
                await Promise.allSettled(
                    unknownParents.map(async (line) => {
                        const product = await fetchProductsDetail({
                            gtin: null,
                            markingCode: line.markingCode,
                        });
                        line.packagingType = product.packagingType;
                    }),
                );
                if (!mounted) return;
                setLines(restoredLines);

                const document =
                    documentResult.status === 'fulfilled' ? documentResult.value : undefined;
                if (document?.status) {
                    setStatus(document.status);
                    setLoadedDocumentId(id);
                } else if (documentResult.status === 'rejected') {
                    showRestError(documentResult.reason as RestError);
                } else {
                    toast.error(t('pages.documents.transfer.statusLoadFailed'));
                }

                const resolvedSide =
                    document?.side ||
                    location.state?.side ||
                    (profile?.legalPerson?.tin
                        ? body.supplier?.tin === profile.legalPerson.tin
                            ? 'OUTGOING'
                            : 'INCOMING'
                        : undefined);
                if (resolvedSide) setSide(resolvedSide);
            })
            .catch((error) => mounted && showRestError(error as RestError))
            .finally(() => mounted && setPending(false));
        return () => {
            mounted = false;
        };
    }, [id, location.state, profile, t]);

    const loadCustomers = useCallback(async (search: string) => {
        const values = await fetchTinLegalName(search ? {combinedSearch: search} : undefined);
        return values.map(
            (value): PartyOption => ({
                label: value.legalName,
                value: value.taxIdNumber,
                companyName: value.legalName ?? '',
                tin: value.taxIdNumber ?? '',
                postalAddress: buildPostalAddress(
                    value.postalCode,
                    value.country,
                    value.city,
                    value.street,
                ),
                streetName: value.street ?? '',
                cityName: value.city ?? '',
                postalZone: value.postalCode ?? '',
                countryCode: value.country ?? '',
            }),
        );
    }, []);

    const loadStorages = useCallback(
        async (search: string) => {
            if (!customer.tin) return [];
            const values = await fetchStorageLocation(customer.tin);
            return values
                .filter((value) =>
                    `${value.storageName} ${value.address}`
                        .toLowerCase()
                        .includes(search.toLowerCase()),
                )
                .map(
                    (value: any): StorageOption => ({
                        label: value.storageName || value.address,
                        value: value.address,
                        storageLocation: {
                            storageLocationName: value.storageName ?? '',
                            storageLocationAddress: value.address ?? '',
                            streetName: value.street ?? value.address ?? '',
                            cityName: value.city ?? customer.cityName,
                            postalZone: value.postalCode ?? customer.postalZone,
                            countryCode: value.countryCode ?? customer.countryCode,
                        },
                    }),
                );
        },
        [customer],
    );

    const buildRequest = (documentLines = lines): TransferDocumentRequest => ({
        documentCurrencyCode: currency,
        issueDate: (issueDate || new Date()).toISOString(),
        supplier: normalizeParty(supplier),
        customer: {
            ...normalizeParty(customer),
            customerStorageLocation: customer.customerStorageLocation,
        },
        transferDocumentLines: flattenTransferDocumentLines(documentLines),
        legalMonetaryTotal: totals,
    });

    const isGeneralValid = Boolean(
        issueDate &&
            supplier.companyName &&
            supplier.tin &&
            currency &&
            customer.companyName &&
            customer.tin &&
            customer.customerStorageLocation.storageLocationName,
    );
    const isLinesValid =
        collectTransferDocumentCodes(lines).length > 0 && areTransferDocumentLinesValid(lines);

    const save = async (submit = false) => {
        if (!editable || pending) return;
        setShowErrors(true);
        if (!isGeneralValid || !isLinesValid) return;
        setPending(true);
        let reportId = id;
        const goToDocument = (documentId: string) => {
            allowNavigation();
            history.replace(generatePath(ROUTE.DOCUMENTS_TRANSFER_DETAIL, {id: documentId}), {
                from: ROUTE.DOCUMENTS_TRANSFER_OUTGOING,
                side: 'OUTGOING',
            });
        };
        try {
            const result = id
                ? await updateTransferDocument(id, buildRequest())
                : await createTransferDocument(buildRequest());
            reportId = id || result.reportId;
            if (!reportId) return;
            const document = await waitUntilTransferDocumentSettled(reportId);
            const didSubmit =
                submit && checkPermission(document.status, side, permissions, 'sendToApproval');

            if (didSubmit) {
                await updateTransferDocumentStatus(reportId, DocumentStatus.UNDER_APPROVAL);
            }

            if (isTransferDocumentReady(document)) {
                toast.success(t('pages.documents.transfer.saved'));
            } else {
                toast.error(t('pages.documents.transfer.statusWaitFailed'));
            }

            setStatus(didSubmit ? DocumentStatus.UNDER_APPROVAL : document.status);
            goToDocument(reportId);
        } catch (error) {
            if (error instanceof Error && error.message === TRANSFER_DOCUMENT_STATUS_TIMEOUT) {
                toast.error(t('pages.documents.transfer.statusWaitFailed'));
            } else {
                showRestError(error as RestError);
            }
            if (reportId) goToDocument(reportId);
        } finally {
            setPending(false);
        }
    };

    const changeStatus = async (nextStatus: DocumentStatus) => {
        if (!id || !statusKnown || pending) return;
        setPending(true);
        try {
            await updateTransferDocumentStatus(id, nextStatus);
            setStatus(nextStatus);
            toast.success(t('pages.documents.invoices.statusChanged', {number: id}));
        } catch (error) {
            showRestError(error as RestError);
        } finally {
            setPending(false);
        }
    };

    const statusActions: Array<{
        action: Parameters<typeof checkPermission>[3];
        status: DocumentStatus;
        label: string;
        variant: ButtonVariant;
    }> = [
        {
            action: 'return',
            status: DocumentStatus.RETURNED,
            label: 'RETURN',
            variant:
                status === DocumentStatus.UNDER_APPROVAL || status === DocumentStatus.APPROVED
                    ? ButtonVariant.Secondary
                    : ButtonVariant.Normal,
        },
        {
            action: 'approve',
            status: DocumentStatus.APPROVED,
            label: 'APPROVE',
            variant: ButtonVariant.Normal,
        },
        {
            action: 'sendToBuyer',
            status: DocumentStatus.PENDING,
            label: 'SEND_TO_BUYER',
            variant: ButtonVariant.Normal,
        },
        {
            action: 'checkFail',
            status: DocumentStatus.CHECK_FAIL,
            label: 'CHECK_FAIL',
            variant: ButtonVariant.Cancel,
        },
        {
            action: 'checkSuccess',
            status: DocumentStatus.CHECK_SUCCESS,
            label: 'CHECK_SUCCESS',
            variant: ButtonVariant.Normal,
        },
        {
            action: 'reject',
            status: DocumentStatus.REJECTED,
            label: 'REJECT',
            variant: ButtonVariant.Cancel,
        },
        {
            action: 'accept',
            status: DocumentStatus.ACCEPTED,
            label: 'ACCEPT',
            variant: ButtonVariant.Normal,
        },
    ];

    const copyId = () => {
        if (!id) return;
        copy(id);
        toast.success(t('pages.documents.details.idCopied'));
    };

    const generalStep = (
        <StepContent>
            <Section>
                <SectionTitle>{t('pages.documents.transfer.sections.general')}</SectionTitle>
                <Grid>
                    <div>
                        <DatePicker
                            label={t('pages.documents.transfer.fields.issueDate')}
                            isRequired
                            disabled={!editable}
                            value={issueDate}
                            selected={issueDate}
                            hasError={showErrors && !issueDate}
                            onChange={(value) => setIssueDate(value)}
                        />
                        <FieldError>
                            {showErrors && !issueDate ? t('Required field') : ''}
                        </FieldError>
                    </div>
                </Grid>
            </Section>
            <Section>
                <SectionTitle>{t('pages.documents.transfer.sections.supplier')}</SectionTitle>
                <Grid columns={3}>
                    <Input
                        label={t('pages.documents.transfer.fields.company')}
                        disabled
                        value={supplier.companyName}
                    />
                    <Input
                        label={t('pages.documents.transfer.fields.tin')}
                        disabled
                        value={supplier.tin}
                    />
                    <Input
                        label={t('pages.documents.transfer.fields.address')}
                        disabled
                        value={supplier.postalAddress}
                    />
                </Grid>
            </Section>
            <Section>
                <SectionTitle>{t('pages.documents.transfer.sections.customer')}</SectionTitle>
                <Grid columns={3}>
                    <div>
                        <AsyncSelect
                            label={t('pages.documents.transfer.fields.company')}
                            isRequired
                            isClearable
                            cacheOptions
                            defaultOptions
                            disabled={!editable}
                            optionComponent={CustomerCompanyNameOrTIN}
                            hasError={showErrors && !customer.tin}
                            loadOptions={loadCustomers}
                            value={
                                customer.tin
                                    ? {label: customer.companyName, value: customer.tin}
                                    : null
                            }
                            onChange={(option: PartyOption | null) => {
                                setCustomer(
                                    option
                                        ? {
                                              ...option,
                                              customerStorageLocation: EMPTY_STORAGE,
                                          }
                                        : EMPTY_CUSTOMER,
                                );
                            }}
                        />
                        <FieldError>
                            {showErrors && !customer.tin ? t('Required field') : ''}
                        </FieldError>
                    </div>
                    <Input
                        label={t('pages.documents.transfer.fields.tin')}
                        disabled
                        value={customer.tin}
                    />
                    <Input
                        label={t('pages.documents.transfer.fields.address')}
                        disabled
                        value={customer.postalAddress}
                    />
                </Grid>
            </Section>
            <Section>
                <SectionTitle>{t('pages.documents.transfer.sections.storage')}</SectionTitle>
                <Grid>
                    <div>
                        <AsyncSelect
                            key={customer.tin || 'no-storage'}
                            label={t('pages.documents.transfer.fields.storage')}
                            isRequired
                            isClearable
                            isSearchable={false}
                            cacheOptions
                            defaultOptions
                            disabled={!editable || !customer.tin}
                            hasError={
                                showErrors && !customer.customerStorageLocation.storageLocationName
                            }
                            loadOptions={loadStorages}
                            value={
                                customer.customerStorageLocation.storageLocationName
                                    ? {
                                          label:
                                              customer.customerStorageLocation.storageLocationName,
                                          value:
                                              customer.customerStorageLocation
                                                  .storageLocationAddress,
                                      }
                                    : null
                            }
                            onChange={(option: StorageOption | null) =>
                                setCustomer((current) => ({
                                    ...current,
                                    customerStorageLocation:
                                        option?.storageLocation || EMPTY_STORAGE,
                                }))
                            }
                        />
                        <FieldError>
                            {showErrors && !customer.customerStorageLocation.storageLocationName
                                ? t('Required field')
                                : ''}
                        </FieldError>
                    </div>
                    <Input
                        label={t('pages.documents.transfer.fields.address')}
                        disabled
                        value={customer.customerStorageLocation.storageLocationAddress}
                    />
                </Grid>
            </Section>
        </StepContent>
    );

    const linesStep = (
        <TransferDocumentLinesStep
            currency={currency}
            editable={editable}
            lines={lines}
            showErrors={showErrors}
            onChange={setLines}
        />
    );

    const totalsStep = (
        <Section>
            <SectionTitle>{t('pages.documents.transfer.sections.totals')}</SectionTitle>
            <Grid columns={4}>
                <Input
                    label={t('pages.documents.transfer.fields.taxExclusive')}
                    disabled
                    value={totals.taxExclusiveAmount}
                />
                <Input
                    label={t('pages.documents.transfer.fields.tax')}
                    disabled
                    value={totals.taxAmount}
                />
                <Input
                    label={t('pages.documents.transfer.fields.taxInclusive')}
                    disabled
                    value={totals.taxInclusiveAmount}
                />
                <Input
                    label={t('pages.documents.transfer.fields.payable')}
                    disabled
                    value={totals.payableAmount}
                />
            </Grid>
        </Section>
    );

    const steps = [
        {label: t('pages.documents.transfer.steps.general'), component: generalStep},
        {label: t('pages.documents.transfer.steps.lines'), component: linesStep},
        {label: t('pages.documents.transfer.steps.totals'), component: totalsStep},
    ];

    if (pending && id && !supplier.tin) return <Spinner />;

    return (
        <>
            <ConfirmNavigationModal
                show={showNavigationModal}
                onConfirm={confirmNavigation}
                onCancel={cancelNavigation}
                doesDocumentExist={Boolean(id)}
                translationPath="pages.documents.transfer"
            />
            <HeadControl>
                <BackTo to={backPath} isBack="">
                    {t('pages.documents.back')}
                </BackTo>
                <CloseAndLinkBack to={backPath}>
                    <CloseAndLinkBackText>
                        {t('pages.documents.invoices.create.buttons.close')}
                    </CloseAndLinkBackText>
                    <CloseAndLinkBackIcon />
                </CloseAndLinkBack>
            </HeadControl>
            <Root>
                <InfoSection>
                    <HeaderLabel>{t('pages.documents.transfer.title')}</HeaderLabel>
                    {id && (
                        <InvoiceIdContainer>
                            <InvoiceId>{id}</InvoiceId>
                            <InvoiceIdCopyButton type="button" onClick={copyId} />
                        </InvoiceIdContainer>
                    )}
                    <Separator />
                    {statusKnown && (
                        <Status
                            color={DOCUMENT_STATUS_COLORS[status] || DOCUMENT_STATUS_COLORS.DEFAULT}
                        >
                            {t(`dictionaries.documents.statusInvoice.${status}`)}
                        </Status>
                    )}
                    {editable && (
                        <Wrapper>
                            <Button size="sm" disabled={pending} onClick={() => save(false)}>
                                {t('pages.documents.transfer.save')}
                            </Button>
                        </Wrapper>
                    )}
                    <HeaderActions>
                        {id &&
                            statusKnown &&
                            statusActions
                                .filter(({action}) =>
                                    checkPermission(status, side, permissions, action),
                                )
                                .map((item) => (
                                    <Button
                                        key={item.action}
                                        size="sm"
                                        disabled={pending}
                                        variant={item.variant}
                                        onClick={() => changeStatus(item.status)}
                                    >
                                        {t(`dictionaries.documents.statusInvoice.${item.label}`)}
                                        {['checkFail', 'reject'].includes(item.action) && (
                                            <Icon
                                                style={{marginLeft: 6}}
                                                source={CrossIcon}
                                                width={10}
                                                height={10}
                                            />
                                        )}
                                        {['checkSuccess', 'accept'].includes(item.action) && (
                                            <Icon
                                                style={{marginLeft: 6}}
                                                source={CheckIcon}
                                                width={16}
                                                height={16}
                                            />
                                        )}
                                    </Button>
                                ))}
                    </HeaderActions>
                </InfoSection>
                <Stepper
                    extraStyles="margin-bottom: 20px;"
                    steps={steps.map((step) => ({...step, ...(!editable && {passed: true})}))}
                    activeStep={activeStep}
                    isClickable={Boolean(id) && !editable}
                    clickCallback={setActiveStep}
                />
                <Footer>
                    {activeStep > 0 && (
                        <Button
                            variant={ButtonVariant.Outline}
                            onClick={() => setActiveStep((value) => value - 1)}
                        >
                            {t('pages.documents.transfer.back')}
                        </Button>
                    )}
                    {activeStep < steps.length - 1 && (
                        <Button
                            onClick={() => {
                                setShowErrors(true);
                                if (editable && activeStep === 0 && !isGeneralValid) return;
                                if (editable && activeStep === 1 && !isLinesValid) return;
                                setShowErrors(false);
                                setActiveStep((value) => value + 1);
                            }}
                        >
                            {t('pages.documents.transfer.next')}
                        </Button>
                    )}
                    {activeStep === steps.length - 1 &&
                        editable &&
                        checkPermission(status, side, permissions, 'sendToApproval') && (
                            <Button
                                isLoading={pending}
                                disabled={pending}
                                onClick={() => save(true)}
                            >
                                {t('pages.documents.transfer.submit')}
                            </Button>
                        )}
                </Footer>
            </Root>
        </>
    );
};
